import { SxProp, styled, useSx } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../common/Icon';
import { BaseText } from '../../common/BaseText';
import { Separator } from '../../common/Separator';
import { useVideoPlayerRewind } from './hooks/useVideoPlayerRewind';

const Container = styled(View)({
  position: 'absolute',
  height: '100%',
  width: '100%',
  zIndex: 1,
  alignItems: 'center',
  justifyContent: 'space-between',
  flexDirection: 'row',
  px: '$8',
});

const arrowContainerStyle: SxProp = {
  borderRadius: 12,
  overflow: 'hidden',
};

const ArrowTextAndIconContainer = styled(View)({
  flexDirection: 'row',
  p: '$5',
  alignItems: 'center',
});

const ArrowContainerBackground = styled(View)({
  position: 'absolute',
  bg: '$background20',
  opacity: 0.4,
  zIndex: -1,
  size: '100%',
});

const Text = styled(BaseText)({
  color: '$background0',
  variant: 'text.xsmall',
  fontWeight: 'semi',
});

export interface IVideoPlayerRewindArrowsProps {
  rewind: ReturnType<typeof useVideoPlayerRewind>;
}

export const VideoPlayerRewindArrows =
  observer<IVideoPlayerRewindArrowsProps>(({ rewind }) => {
    const { t } = useTranslation();
    const sx = useSx();
    const leftArrowStyle = useAnimatedStyle(() => ({
      opacity: rewind.leftArrowOpacity.value,
    }));
    const rightArrowStyle = useAnimatedStyle(() => ({
      opacity: rewind.rightArrowOpacity.value,
    }));

    return (
      <Container pointerEvents="none">
        <Animated.View
          style={[sx(arrowContainerStyle), leftArrowStyle]}
        >
          <ArrowContainerBackground />
          <ArrowTextAndIconContainer>
            <Icon
              name="previous-bold"
              size={20}
              color="$background0"
            />
            <Separator size="$4" vertical />
            <Text>5 {t('common.videoPlayer.sec')}</Text>
          </ArrowTextAndIconContainer>
        </Animated.View>

        <Animated.View
          style={[sx(arrowContainerStyle), rightArrowStyle]}
        >
          <ArrowContainerBackground />
          <ArrowTextAndIconContainer>
            <Text>5 {t('common.videoPlayer.sec')}</Text>
            <Separator size="$4" vertical />
            <Icon name="next-bold" size={20} color="$background0" />
          </ArrowTextAndIconContainer>
        </Animated.View>
      </Container>
    );
  });
